import React, { useState } from 'react';
import { Box, Typography, Card, CardContent } from '@mui/material';
import ReceiptIcon from '@mui/icons-material/Receipt';
import EmailIcon from '@mui/icons-material/Email';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import EuroIcon from '@mui/icons-material/Euro';
import LocationOnIcon from '@mui/icons-material/LocationOn';

interface HowItWorksStep {
    number: string;
    title: string;
    description: string;
    image?: string;
    icons?: React.ReactNode;
}

const steps: HowItWorksStep[] = [
    {
        number: '1',
        title: 'Įkelkite kvitus',
        description: 'Įkelkite kvitus rankiniu būdu arba automatiškai iš el. pašto. Sistema automatiškai nuskaito visą informaciją iš jūsų kvitų.',
        image: '/img/how-it-works-1.png', // Receipt and envelope icon
        icons: (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'center' }}>
                <ReceiptIcon sx={{ fontSize: 40, color: '#3b82f6' }} />
                <EmailIcon sx={{ fontSize: 40, color: '#3b82f6', opacity: 0.8 }} />
            </Box>
        ),
    },
    {
        number: '2',
        title: 'Gaukite personalizuotą analizę',
        description: 'Gaukite išsamią analizę apie savo pirkimus: kalorijas, maistinę vertę, įpročius ir kainas kiekvienam produktui jūsų ir jūsų šeimos nariams.',
        image: '/img/how-it-works-2.png', // Checklist, shopping cart, and analytics graph icon
        icons: (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'center' }}>
                <AssessmentIcon sx={{ fontSize: 40, color: '#3b82f6' }} />
                <ShoppingCartIcon sx={{ fontSize: 40, color: '#3b82f6', opacity: 0.8 }} />
                <AnalyticsIcon sx={{ fontSize: 40, color: '#3b82f6', opacity: 0.6 }} />
            </Box>
        ),
    },
    {
        number: '3',
        title: 'Apsipirkite išmaniau',
        description: 'Matykite, kur jūsų įprastas apsipirkimo krepšelis kainuoja pigiausiai – pagal realius kvitus ir jūsų vietovę.',
        image: '/img/how-it-works-3.png', // Euro symbol, shopping basket, and map pin icon
        icons: (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'center' }}>
                <EuroIcon sx={{ fontSize: 40, color: '#3b82f6' }} />
                <ShoppingCartIcon sx={{ fontSize: 40, color: '#3b82f6', opacity: 0.8 }} />
                <LocationOnIcon sx={{ fontSize: 40, color: '#3b82f6', opacity: 0.6 }} />
            </Box>
        ),
    },
];

// Component to handle image loading with fallback to icons
const IllustrationBox: React.FC<{ step: HowItWorksStep }> = ({ step }) => {
    const [imageError, setImageError] = useState(false);

    return (
        <Box
            sx={{
                width: '100%',
                height: '300px',
                mb: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `
                    radial-gradient(300px 150px at 30% 20%, rgba(59, 130, 246, .08), transparent 60%),
                    radial-gradient(300px 150px at 70% 0%, rgba(34, 197, 94, .08), transparent 60%),
                    linear-gradient(180deg, #fff 0%, #f8fafc 100%)
                `,
                borderRadius: '12px',
                position: 'relative',
            }}
        >
            {step.image && !imageError ? (
                <Box
                    component="img"
                    src={step.image}
                    alt={step.title}
                    onError={() => {
                        setImageError(true);
                    }}
                    sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                    }}
                />
            ) : (
                step.icons
            )}
        </Box>
    );
};

const HowItWorksSection: React.FC = () => {
    return (
        <Box
            sx={{
                py: { xs: 4, sm: 6 },
                px: { xs: 2, sm: 3 },
            }}
        >
            <Typography
                variant="h4"
                component="h2"
                sx={{
                    textAlign: 'center',
                    fontWeight: 900,
                    fontSize: { xs: '24px', sm: '28px', md: '32px' },
                    letterSpacing: '-.2px',
                    color: '#111827',
                    mb: { xs: 3, sm: 4 },
                }}
            >
                Kaip tai veikia
            </Typography>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: { xs: 2, sm: 3, md: 4 },
                    width: '100%',
                }}
            >
                {steps.map((step, index) => (
                    <Card
                        key={index}
                        sx={{
                            background: '#ffffff',
                            border: '1px solid rgba(17,24,39,.10)',
                            borderRadius: '14px',
                            boxShadow: '0 8px 18px rgba(17,24,39,.08)',
                            transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                            display: 'flex',
                            flexDirection: 'column',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 12px 24px rgba(17,24,39,.12)',
                            },
                        }}
                    >
                        <CardContent
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                textAlign: 'center',
                                p: { xs: 3, sm: 4 },
                                flexGrow: 1,
                            }}
                        >
                            {/* Illustration Image or Icons */}
                            <IllustrationBox step={step} />

                            {/* Title */}
                            <Typography
                                variant="h6"
                                component="h3"
                                sx={{
                                    fontWeight: 900,
                                    fontSize: { xs: '18px', sm: '20px' },
                                    color: '#111827',
                                    mb: 2,
                                }}
                            >
                                {step.title}
                            </Typography>

                            {/* Description */}
                            <Typography
                                variant="body2"
                                sx={{
                                    color: '#6b7280',
                                    fontSize: { xs: '14px', sm: '15px' },
                                    lineHeight: 1.6,
                                }}
                            >
                                {step.description}
                            </Typography>
                        </CardContent>
                    </Card>
                ))}
            </Box>
        </Box>
    );
};

export default HowItWorksSection;

